import React, { useEffect, useState } from 'react';
import { GlobeIcon } from '../../../assets/Icons';
import { http_delete, http_get, http_put } from '../../utils/api_client';
import { API_ENDPOINTS } from '../../../pages/utils/endpoints';
import { getCompany } from '../../../../redux/companySlice';
import { useSelector } from 'react-redux';
import Loader from '../../../organisms/loader/Loader';
import AddDomainWizard from './components/AddDomainWizard';
import './DomainPage.scss';

// BL-070 — the custom domains list, matching the Custom Domain redesign mockup.
// Add / Edit / Fix open the 4-step wizard (BL-068/069).

const POLL_MS = 10000;

const STATUS_META = {
  verified: { cls: 'ok', label: 'Verified' },
  verifying: { cls: 'ver', label: 'Verifying…' },
  error: { cls: 'err', label: 'Error' },
  inactive: { cls: 'off', label: 'Inactive' }
};

const timeAgo = (iso) => {
  if (!iso) return null;
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const DomainPage = () => {
  const [domains, setDomains] = useState([]);
  const [limits, setLimits] = useState({ used: 0, limit: null, plan_key: null, cname_target: '', a_target: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [wizard, setWizard] = useState(null); // { mode, domain }
  const company = useSelector((state) => getCompany(state));

  useEffect(() => {
    getDomains();
    getLimits();
  }, []);

  // Live auto-detect: while any domain is verifying, re-poll every 10s.
  useEffect(() => {
    if (!domains.some((d) => d.verification_status === 'verifying')) return undefined;
    const timer = setInterval(() => { getDomains(true); getLimits(); }, POLL_MS);
    return () => clearInterval(timer);
  }, [domains]);

  const getDomains = async (silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      const response = await http_get(API_ENDPOINTS.GET_DOMAINS);
      if (response.status === 200) setDomains(response.data || []);
    } catch (e) {
      console.error('Error fetching domains:', e);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const getLimits = async () => {
    try {
      const response = await http_get(API_ENDPOINTS.DOMAIN_LIMITS);
      if (response.status === 200) setLimits(response.data || {});
    } catch (e) {
      console.error('Error fetching domain limits:', e);
    }
  };

  const closeWizard = () => setWizard(null);
  const completeWizard = () => { setWizard(null); getDomains(true); getLimits(); };

  const handleSignIn = (id) => {
    window.location.pathname = `/${company?.slug}/domains/${id}/domain_page_configuration`;
  };

  const handleRemove = async (domain) => {
    if (!confirm(`Remove ${domain.name}? This can't be undone.`)) return;
    try {
      const response = await http_delete(API_ENDPOINTS.DELETE_DOMAIN(domain.id));
      if (response.status === 200) { getDomains(true); getLimits(); }
    } catch (e) {
      console.error('Error removing domain:', e);
    }
  };

  const handleSetPrimary = async (domain) => {
    try {
      await http_put(API_ENDPOINTS.SET_PRIMARY_DOMAIN(domain.id), {});
      getDomains(true);
    } catch (e) {
      console.error('Error setting primary domain:', e);
    }
  };

  const limitLabel = (limits.limit === null || limits.limit === undefined) ? 'unlimited' : limits.limit;
  const showUpgrade = limits.limit !== null && limits.limit !== undefined && limits.limit < 10;

  const renderRow = (domain) => {
    const meta = STATUS_META[domain.verification_status] || STATUS_META.verifying;
    const isError = domain.verification_status === 'error';
    const isVerifying = domain.verification_status === 'verifying';
    const verified = domain.verification_status === 'verified';

    let lastVerified = '—';
    if (isVerifying) lastVerified = 'checking now';
    else if (verified) lastVerified = timeAgo(domain.verified_at || domain.last_checked_at) || '—';

    return (
      <React.Fragment key={domain.id}>
        <tr>
          <td><span className='dom'><GlobeIcon />{domain.name}</span></td>
          <td><span className={`pill ${meta.cls}`}><span className='dot' />{meta.label}</span></td>
          <td>
            {domain.is_primary
              ? <span className='tag'>Primary</span>
              : verified
                ? <a className='linkish' onClick={() => handleSetPrimary(domain)}>Set as primary</a>
                : <span className='muted'>—</span>}
          </td>
          <td className='muted'>{lastVerified}</td>
          <td>
            <div className='acts'>
              {isError
                ? <a onClick={() => setWizard({ mode: 'fix', domain })}>Fix</a>
                : <a onClick={() => handleSignIn(domain.id)}>Sign-in page</a>}
              <a onClick={() => setWizard({ mode: 'edit', domain })}>Edit</a>
              <a className='rm' onClick={() => handleRemove(domain)}>Remove</a>
            </div>
          </td>
        </tr>
        {isError && domain.last_error && (
          <tr className='errrow'>
            <td colSpan={5}>
              <div className='errbox'><span className='ei'>!</span><div>{domain.last_error}</div></div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className='un-domains'>
      {wizard && (
        <AddDomainWizard
          mode={wizard.mode}
          existingDomain={wizard.domain}
          targets={{ cname_target: limits.cname_target, a_target: limits.a_target }}
          onClose={closeWizard}
          onComplete={completeWizard}
        />
      )}

      <div className='un-domains__head'>
        <div>
          <h4>Custom domains</h4>
          <p>Serve your walkthroughs and paths from your own branded URL.</p>
        </div>
        <button className='btn btn-primary' onClick={() => setWizard({ mode: 'add', domain: null })} type='button'>Add domain</button>
      </div>

      <div className='un-domains__usage'>
        Using <b>{limits.used} of {limitLabel}</b> domain{limits.used === 1 ? '' : 's'}
        {showUpgrade && <span className='upgrade'> · Upgrade to Premium for 10</span>}
      </div>

      <table className='un-domains__table'>
        <thead>
          <tr><th>Domain</th><th>Status</th><th>Primary</th><th>Last verified</th><th></th></tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr><td colSpan={5}><Loader /></td></tr>
          ) : domains.length > 0 ? (
            domains.map(renderRow)
          ) : (
            <tr><td colSpan={5} className='empty'>No custom domains yet. Add one to serve from your own branded URL.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DomainPage;
